import clsx from 'clsx';
import * as React from 'react';

import type { EditAnalyticsBlockType } from '@gitbook/app-analytics/common';
import {
    BLOCK_CONTENT_REF,
    BLOCK_LIST_ITEM,
    BLOCK_REUSABLE_CONTENT,
    canInsertBlockIn,
    contentRef,
    reusableContent,
} from '@gitbook/doc-core';
import { dropAfter, dropBefore, dropHorizontal, type PaletteDef } from '@gitbook/react-uikit';
import {
    type AncestorNode,
    type Block,
    BlockRange,
    type Change,
    type Document,
    type Fragment,
    type Value,
} from '@gitbook/slate';
import type { BlockProps, EditorContainer } from '@gitbook/slate-react';
import type { IconDefinition } from '@gitbook/spine-react';
import { checkIsKeyPressed, useIsKeyPressed, useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import {
    getContentRefForDND,
    getDragItem,
    useDroppableWithCallback,
    usePersistentDraggable,
} from '../../../DragAndDrop';
import { BlockLayout } from '../../BlockLayout';
import { ContentEditorContext } from '../../contexts';
import { EditorWidthLayout } from '../../EditorWidth';
import { PageLayoutWidthContext } from '../../PageLayout';
import { ReusableContentSuggestionPrompt } from '../../reusable-content';
import {
    BlockControls,
    type BlockControlsRef,
    InsertButton,
    OptionsButton,
} from '../blockControls';
import { useBlockPaletteFactory } from '../blockPalette';
import { BlockConflictsWrapper } from '../conflicts';
import { useBlockHover, useIsLastBlockHovered } from './hovered';
import { type BlockLayout as BlockLayoutType, useBlockLayout } from './useBlockLayout';

export const DEFAULT_WIDTH = 600;
export const WIDE_WIDTH = 900;

export type BlockWrapperProps = BlockProps & {
    /** HTML tag to use */
    tag?: React.ElementType;

    /** Props to pass the top-level content view */
    contentProps?: React.ComponentProps<'div'>;

    /** Icon to use in the commands button */
    commandIcon?: IconDefinition;
    commandIconAnimatedAngle?: number;

    /** Palette to use for this block. */
    palette?: () => PaletteDef;

    /** Optional className to apply on the block wrapper container */
    className?: string;
};

/**
 * Wrapper around a block.
 */
export const BlockWrapper = React.forwardRef(function BlockWrapper(
    props: BlockWrapperProps,
    ref: React.ForwardedRef<BlockControlsRef>
): React.ReactElement {
    const { tag, contentProps, commandIcon, commandIconAnimatedAngle, palette, ...blockProps } =
        props;
    const {
        node,
        ancestors,
        editor,
        attributes,
        domRef,
        isFocused,
        children,
        isSelectedBlock,
        isBlockSelection,
    } = blockProps;
    const { readOnly } = editor;
    const isHovered = useIsLastBlockHovered(node.key);

    const {
        rightGutterComponent: RightGutterComponent,
        blockHighlightComponent: BlockHighlightComponent,
        logAnalyticsEvent,
        onBlockHover,
    } = React.useContext(ContentEditorContext);

    const pageLayoutWidth = React.useContext(PageLayoutWidthContext);

    const layout = useBlockLayout({
        node,
        ancestors,
        isBlockSelection,
        readOnly,
        pageLayoutWidth,
    });
    const blockLayoutWidthClassName =
        pageLayoutWidth === 'wide'
            ? 'max-w-full'
            : layout.blockFullWidth
              ? 'max-w-full'
              : 'max-w-[--block-wrapper-max-width] w-full mx-auto';

    const alignData = node.data.toFrozenObject().align || 'start';
    const blockAlignmentStyle = {
        alignSelf: {
            start: 'flex-stretch',
            center: 'center',
            end: 'flex-end',
        }[alignData],
        justifyContent: {
            start: 'flex-start',
            center: 'center',
            end: 'flex-end',
        }[alignData],
    };

    const innerContent = (
        <div
            {...contentProps}
            data-block-content={node.key}
            className={clsx(
                'relative flex-1',
                layout.blockFullWidth ? 'max-w-full' : 'max-w-[--block-wrapper-max-width]',
                {
                    'block-highlight': !readOnly && isBlockSelection,
                },
                contentProps?.className
            )}
            data-selected={!readOnly && isSelectedBlock ? '' : undefined}
        >
            {BlockHighlightComponent ? <BlockHighlightComponent nodeKey={node.key} /> : null}
            <ReusableContentSuggestionPrompt nodeKey={node.key} />
            {children}
        </div>
    );

    const { containerProps } = useBlockHover({ nodeKey: node.key, onBlockHover });

    const CompTag = tag ?? 'div';

    // In readonly we do not want to show the controls.
    if (readOnly) {
        return (
            <BlockLayout
                renderMode={layout.renderMode}
                className={blockLayoutWidthClassName}
                {...containerProps}
            >
                <EditorWidthLayout>
                    <CompTag
                        {...attributes}
                        ref={domRef}
                        className={clsx(
                            'relative flex w-full',
                            layout.blockStyle.className,
                            props.className
                        )}
                        style={{ ...layout.blockStyle.style, ...blockAlignmentStyle }}
                    >
                        {/* Right gutter button */}
                        {RightGutterComponent && layout.rightGutterComponent ? (
                            <div
                                className={clsx(
                                    'flex',
                                    layout.rightGutterComponent.hitArea.className
                                )}
                                style={layout.rightGutterComponent.hitArea.style}
                            >
                                <RightGutterComponent
                                    nodeKey={node.key}
                                    visible={isHovered}
                                    shortcutsActive={isFocused}
                                    buttonSize={layout.rightGutterComponent.size}
                                    style={layout.rightGutterComponent.style}
                                    readOnly={readOnly}
                                />
                            </div>
                        ) : null}
                        {innerContent}
                    </CompTag>
                </EditorWidthLayout>
            </BlockLayout>
        );
    }

    return (
        <DraggableBlockWrapper
            key={node.key}
            ref={ref}
            layout={layout}
            isHovered={isHovered}
            RightGutterComponent={RightGutterComponent}
            logAnalyticsEvent={logAnalyticsEvent}
            blockAlignmentStyle={blockAlignmentStyle}
            blockLayoutWidthClassName={blockLayoutWidthClassName}
            {...containerProps}
            {...props}
        >
            {innerContent}
        </DraggableBlockWrapper>
    );
});

/**
 *  BlockWrapper when not in readonly mode, i.e when the block is editable,
 *  acts as both a draggable object and a drop target.
 *
 *  Partly, the intention of separating out the draggable version of the BlockWrapper
 *  from the readonly version was to avoid setting up drag and drop refs with dnd
 *  until they are absolutely needed and to keep the target and source more stable.
 */
const DraggableBlockWrapper = React.memo(
    React.forwardRef(function DraggableBlockWrapper(
        props: BlockWrapperProps & {
            layout: BlockLayoutType;
            isHovered: boolean;
            RightGutterComponent: any;
            logAnalyticsEvent: any;
            onMouseLeave: React.MouseEventHandler;
            onMouseEnter: React.MouseEventHandler;
            blockAlignmentStyle: React.CSSProperties;
            blockLayoutWidthClassName?: string;
        },
        ref: React.ForwardedRef<BlockControlsRef>
    ) {
        const {
            tag,
            commandIcon,
            commandIconAnimatedAngle,
            palette: paletteProp,
            layout,
            isHovered,
            RightGutterComponent,
            logAnalyticsEvent,
            blockLayoutWidthClassName,
            ...blockProps
        } = props;
        const {
            node,
            parent,
            editor,
            attributes,
            domRef,
            isFocused,
            children,
            isSelectedBlock,
            isBlockSelection,
            blockAlignmentStyle,
        } = blockProps;
        const { readOnly } = editor;

        const isExpandedBlockSelection = isBlockSelection && editor.value.selection.isExpanded;
        const paletteWithAI = useBlockPaletteFactory({ nodeKey: node.key, aiCompletion: true });
        const optionsPalette = isExpandedBlockSelection ? paletteWithAI : paletteProp;

        // Drag and drop
        const drag = usePersistentDraggable({
            disabled: readOnly,
            ...getDragItem(
                isExpandedBlockSelection
                    ? {
                          type: 'block-range',
                          id: node.key,
                          previewLabel: (
                              <DndLabelPreview
                                  label={(isAltPressed) =>
                                      `${isAltPressed ? 'Copy' : 'Move'} blocks`
                                  }
                              />
                          ),
                      }
                    : {
                          type: 'block',
                          id: node.key,
                          blockType: node.type,
                          previewLabel: (
                              <DndLabelPreview
                                  label={(isAltPressed) => {
                                      const verb = isAltPressed ? 'Copy' : 'Move';
                                      return node.type === 'images'
                                          ? parent.nodes.size > 1
                                              ? `${verb} images`
                                              : `${verb} image`
                                          : `${verb} block`;
                                  }}
                              />
                          ),
                      }
            ),
        });

        const [position, setPosition] = React.useState<'before' | 'after'>();

        // Dropping blocks
        const drop = useDroppableWithCallback({
            shouldAccept: (item) => {
                switch (item.type) {
                    case 'block':
                        return (
                            // If the node is not in the document, we cannot drop it.
                            // It happens often with reusable content blocks.
                            editor.value.document.hasNode(item.id) &&
                            // If we drop a block on itself, we should not accept it.
                            item.id !== node.key &&
                            // The parent should accept the block.
                            canInsertBlockIn(parent, item.blockType)
                        );
                    case 'block-range':
                        return !editor.value.blocks.some((block) => block.key === node.key);

                    case 'page':
                    case 'space':
                    case 'collection':
                        return canInsertBlockIn(parent, BLOCK_CONTENT_REF);

                    case 'reusable-content':
                        return canInsertBlockIn(parent, BLOCK_REUSABLE_CONTENT);

                    default:
                        return false;
                }
            },
            onDrop: (item) => {
                editor.change((change) => {
                    switch (item.type) {
                        case 'block-range': {
                            moveOrCopyBlocks({
                                editor,
                                change,
                                node,
                                parent,
                                position: position || 'after',
                                operation: checkIsKeyPressed('Alt') ? 'copy' : 'move',
                            });
                            logAnalyticsEvent({
                                type: 'edit_page_document_move_block_range',
                                method: 'dnd',
                                rangeSize: change.value.blocks.size,
                            });
                            return;
                        }
                        case 'block': {
                            const fromParent = change.value.document.getParent(item.id);
                            assert(fromParent);
                            const block = change.value.document.getNode(item.id);
                            assert(block && block.object === 'block');
                            change.select(BlockRange.createAtBlock(block));
                            moveOrCopyBlocks({
                                editor,
                                change,
                                node,
                                parent,
                                position: position || 'after',
                                operation: checkIsKeyPressed('Alt') ? 'copy' : 'move',
                            });
                            const movedBlockKey = change.value.blocks.first()?.key;
                            const toParent = movedBlockKey
                                ? change.value.document.getParent(movedBlockKey)
                                : null;
                            logAnalyticsEvent({
                                type: 'edit_page_document_move_block',
                                blockType: item.blockType as EditAnalyticsBlockType,
                                method: 'dnd',
                                toParent:
                                    toParent?.object === 'block'
                                        ? (toParent?.type as EditAnalyticsBlockType)
                                        : toParent?.object,
                                fromParent:
                                    fromParent.object === 'block'
                                        ? (fromParent.type as EditAnalyticsBlockType)
                                        : fromParent.object,
                            });
                            return;
                        }
                        case 'reusable-content': {
                            logAnalyticsEvent({
                                type: 'reusable_content_insert',
                                method: 'dnd',
                            });
                            const reusableContentBlock = reusableContent.createReusableContentBlock(
                                {
                                    kind: 'reusable-content',
                                    reusableContent: item.id,
                                }
                            );
                            const method = {
                                before: 'insertNodeBeforeByKey' as const,
                                after: 'insertNodeAfterByKey' as const,
                            }[position ?? 'after'];

                            change[method](node.key, reusableContentBlock);
                            return;
                        }
                        default: {
                            const refBlock = contentRef.createContentRefBlock(
                                getContentRefForDND(item)
                            );
                            if (position === 'before') {
                                change.insertNodeBeforeByKey(node.key, refBlock);
                            } else {
                                change.insertNodeAfterByKey(node.key, refBlock);
                            }
                        }
                    }
                });
            },
            onHoverMove: ({ yPosition }) => {
                const newPosition = yPosition === 'bottom' ? 'after' : 'before';
                if (position !== newPosition) {
                    setPosition(newPosition);
                }
            },
        });

        const { rightGutter: rightGutterIsInteractive, controls: controlsIsInteractive } =
            useIsInteractive({
                isBlockSelection,
                isDragging: drag.isDragging,
                isSelectedBlock,
                readOnly,
                value: editor.value,
                node,
            });

        const mergedRef = useMergedRefs(drop.setNodeRef, domRef);

        const CompTag = tag ?? 'div';

        return (
            <BlockConflictsWrapper node={node} editor={editor}>
                <BlockLayout
                    renderMode={layout.renderMode}
                    onMouseEnter={props.onMouseEnter}
                    onMouseLeave={props.onMouseLeave}
                    className={blockLayoutWidthClassName}
                >
                    <EditorWidthLayout className="flex flex flex-col">
                        <CompTag
                            {...attributes}
                            ref={mergedRef}
                            className={clsx(
                                'relative flex w-full',
                                drag.isDragging && 'opacity-50',
                                layout.blockStyle.className,
                                dropHorizontal,
                                drop.isOver && {
                                    [dropBefore]: position === 'before',
                                    [dropAfter]: position === 'after',
                                },
                                props.className
                            )}
                            style={{ ...layout.blockStyle.style, ...blockAlignmentStyle }}
                        >
                            {/* Commands */}
                            {controlsIsInteractive && layout.controlButtons ? (
                                <BlockControls isHovered={isHovered} layout={layout.controlButtons}>
                                    <InsertButton
                                        ref={ref}
                                        editor={editor}
                                        parent={parent}
                                        node={node}
                                        withInsertButton={layout.controlButtons.withInsertButton}
                                        size={layout.controlButtons.size}
                                    />
                                    {layout.controlButtons.withOptionsButton ? (
                                        <OptionsButton
                                            ref={drag.setNodeRef}
                                            isFocused={isFocused}
                                            isDragging={drag.isDragging}
                                            dragListeners={drag.listeners}
                                            editor={editor}
                                            node={node}
                                            parent={parent}
                                            palette={optionsPalette}
                                            size={layout.controlButtons.size}
                                        />
                                    ) : null}
                                </BlockControls>
                            ) : null}
                            {/* Right gutter button */}
                            {rightGutterIsInteractive &&
                            RightGutterComponent &&
                            layout.rightGutterComponent ? (
                                <div
                                    contentEditable={false}
                                    className={clsx(
                                        'group/gutter flex',
                                        layout.rightGutterComponent.hitArea.className
                                    )}
                                    style={layout.rightGutterComponent.hitArea.style}
                                >
                                    <RightGutterComponent
                                        nodeKey={node.key}
                                        visible={isHovered}
                                        shortcutsActive={isFocused}
                                        buttonSize={layout.rightGutterComponent.size}
                                        style={layout.rightGutterComponent.style}
                                        readOnly={readOnly}
                                    />
                                </div>
                            ) : null}
                            {/* Content */}
                            {children}
                        </CompTag>
                    </EditorWidthLayout>
                </BlockLayout>
            </BlockConflictsWrapper>
        );
    })
);

function moveOrCopyBlocks(args: {
    editor: EditorContainer;
    change: Change;
    node: Block;
    parent: Block | Document | Fragment;
    position: 'before' | 'after';
    operation: 'move' | 'copy';
}) {
    const { editor, change, node, position, operation } = args;
    if (operation === 'copy') {
        change.duplicateBlocks();
    }

    change.moveBlockRangeSelectionTo(node.key, position === 'after' ? 1 : 0, {
        normalize: false,
    });

    const blocks = change.value.blocks;

    const parents: Map<string, AncestorNode> = new Map();
    blocks.forEach((block) => {
        const parent = change.value.document.getParent(block.key);
        assert(parent);
        parents.set(block.key, parent);
    });

    blocks.forEach((block) => {
        const parent = parents.get(block.key);
        assert(parent);
        const modified = editor.stack.find('prepareBlockForTransport', block, parent);

        if (modified) {
            change.replaceNodeByKey(block.key, modified, { normalize: false });
        }
    });

    // Finally normalize everything.
    change.normalize();

    const firstBlock = blocks.first();
    const lastBlock = blocks.last();

    if (firstBlock && lastBlock) {
        change
            .selectBlock(
                BlockRange.create({
                    anchorBlock: firstBlock.key,
                    focusBlock: lastBlock.key,
                })
            )
            .focus();
    }
}

function DndLabelPreview(props: { label: (isAltPressed: boolean) => string }) {
    const isAltPressed = useIsKeyPressed('Alt');
    return (
        <div className="relative text-center">
            <div>{props.label(isAltPressed)}</div>
            <div className="invisible h-0">{props.label(true)}</div>
            <div className="invisible h-0">{props.label(false)}</div>
        </div>
    );
}

/**
 * Check if the block should have its controls button visible.
 */
function useIsInteractive(props: {
    isDragging: boolean;
    isBlockSelection: boolean;
    isSelectedBlock: boolean;
    readOnly: boolean;
    value: Value;
    node: Block;
}): {
    controls: boolean;
    diff: boolean;
    rightGutter: boolean;
} {
    const { isDragging, isBlockSelection, isSelectedBlock, readOnly, value, node } = props;
    const { selection } = value;

    return React.useMemo(() => {
        if (readOnly) {
            return { controls: false, diff: false, rightGutter: true };
        }

        if (isBlockSelection) {
            // When a list block with multiple items is selected, selection is only on the list block parent
            // while only `list-items` have block options so we need to manually show the controls.
            const shouldShowControls = node.type === BLOCK_LIST_ITEM || isSelectedBlock;

            return {
                controls: shouldShowControls,
                diff: true,
                rightGutter: false,
            };
        }

        return {
            controls: !selection.isExpanded,
            diff: true,
            rightGutter: !selection.isExpanded,
        };
    }, [selection.isExpanded, isDragging, isBlockSelection, isSelectedBlock, readOnly]);
}
